import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { AccountTypeBadge } from '@/components/common/AccountTypeBadge'
import { UserRoleBadge } from '@/components/common/UserRoleBadge'
import { useAccount } from '@/hooks/useAccounts'
import { useDevices } from '@/hooks/useDevices'
import { useAuthStore } from '@/stores/authStore'
import { accountService } from '@/services/account.service'
import { weatherService, type CurrentWeatherResponse, type ForecastWeatherResponse } from '@/services/weather.service'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowLeft, Edit, FileText, Building, Users, Cloud, History, Plus, Trash2, Mail, RefreshCw, UserCheck, UserX, Archive, RotateCcw } from 'lucide-react'
import { formatUTCToLocal } from '@/utils/date'
import { useToast } from '@/components/ui/use-toast'
import { useWebLogs } from '@/hooks/useWebLogs'
import { format, parseISO } from 'date-fns'
import type { Account, AccountUser, AccountInvite } from '@/types/account.types'

const AccountActivityLog = ({ accountId }: { accountId: string }) => {
  const { data: logs, isLoading } = useWebLogs({
    account_id: accountId,
    days: 30,
    page_size: 100,
  })

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!logs || logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No activity logs found</p>
        <p className="text-sm mt-2">Activity for this account will appear here</p>
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Details</TableHead>
          <TableHead>User</TableHead>
          <TableHead>IP</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {logs.map((log) => (
          <TableRow key={log.id}>
            <TableCell className="whitespace-nowrap">
              {format(parseISO(log.log_at), 'MMM d, yyyy HH:mm')}
            </TableCell>
            <TableCell>
              <Badge variant="outline">{log.type}</Badge>
            </TableCell>
            <TableCell className="max-w-[250px] truncate">
              {log.log_data || '-'}
            </TableCell>
            <TableCell>
              {log.logged_by_user?.display_name || log.user?.display_name || log.log_by || '-'}
            </TableCell>
            <TableCell className="font-mono text-xs">
              {log.ip_addr || '-'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export const AccountDetailPage = () => {
  const { accountId } = useParams<{ accountId: string }>()
  const { data: account, isLoading, error } = useAccount(accountId!)
  const { data: devices, error: devicesError, isLoading: devicesLoading } = useDevices({ accountId })
  const { user } = useAuthStore()
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState('details')

  // Sub-accounts state
  const [subAccounts, setSubAccounts] = useState<Account[]>([])
  const [loadingSubAccounts, setLoadingSubAccounts] = useState(false)

  // Users state
  const [accountUsers, setAccountUsers] = useState<AccountUser[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  // Invitations state
  const [invitations, setInvitations] = useState<AccountInvite[]>([])
  const [inviteEmail, setInviteEmail] = useState('')

  // Weather state
  const [currentWeather, setCurrentWeather] = useState<CurrentWeatherResponse | null>(null)
  const [forecast, setForecast] = useState<ForecastWeatherResponse | null>(null)
  const [loadingWeather, setLoadingWeather] = useState(false)
  const [weatherError, setWeatherError] = useState<string | null>(null)

  // Load sub-accounts
  useEffect(() => {
    if (accountId && activeTab === 'subaccounts') {
      loadSubAccounts()
    }
  }, [accountId, activeTab])

  // Load users and invitations
  useEffect(() => {
    if (accountId && activeTab === 'users') {
      loadUsers()
      loadInvitations()
    }
  }, [accountId, activeTab])

  // Load weather
  useEffect(() => {
    if (accountId && activeTab === 'weather') {
      loadWeather()
    }
  }, [accountId, activeTab])

  const loadSubAccounts = async () => {
    if (!accountId) return
    setLoadingSubAccounts(true)
    try {
      const data = await accountService.getSubAccounts(accountId)
      setSubAccounts(data)
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load sub-accounts', variant: 'destructive' })
    } finally {
      setLoadingSubAccounts(false)
    }
  }

  const loadUsers = async () => {
    if (!accountId) return
    setLoadingUsers(true)
    try {
      const data = await accountService.getAccountUsers(accountId)
      setAccountUsers(data.users)
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load users', variant: 'destructive' })
    } finally {
      setLoadingUsers(false)
    }
  }

  const loadInvitations = async () => {
    if (!accountId) return
    try {
      const data = await accountService.getInvitations(accountId)
      setInvitations(data.invites)
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load invitations', variant: 'destructive' })
    }
  }

  const loadWeather = async () => {
    if (!accountId) return
    setLoadingWeather(true)
    setWeatherError(null)
    try {
      const [current, forecastData] = await Promise.all([
        weatherService.getCurrentWeather(accountId),
        weatherService.getForecast(accountId)
      ])
      setCurrentWeather(current)
      setForecast(forecastData)
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to load weather data'
      setWeatherError(message)
    } finally {
      setLoadingWeather(false)
    }
  }

  const handleRemoveSubAccount = async (childAccountId: string) => {
    if (!accountId || !confirm('Remove this sub-account?')) return
    try {
      await accountService.unlinkSubAccount(accountId, childAccountId)
      toast({ title: 'Success', description: 'Sub-account removed' })
      loadSubAccounts()
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to remove sub-account', variant: 'destructive' })
    }
  }

  const handleToggleAdmin = async (userId: string, currentIsAdmin: boolean) => {
    if (!accountId) return
    try {
      await accountService.toggleUserAdmin(accountId, userId, !currentIsAdmin)
      toast({ title: 'Success', description: `Admin status ${!currentIsAdmin ? 'granted' : 'revoked'}` })
      loadUsers()
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update admin status', variant: 'destructive' })
    }
  }

  const handleRemoveUser = async (userId: string) => {
    if (!accountId || !confirm('Remove this user from the account?')) return
    try {
      await accountService.unlinkUser(accountId, userId)
      toast({ title: 'Success', description: 'User removed' })
      loadUsers()
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to remove user', variant: 'destructive' })
    }
  }

  const handleInviteUser = async () => {
    if (!accountId || !inviteEmail.trim()) return
    try {
      await accountService.inviteUser(accountId, inviteEmail)
      toast({ title: 'Success', description: 'Invitation sent' })
      setInviteEmail('')
      loadInvitations()
    } catch (err: any) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed to send invitation', variant: 'destructive' })
    }
  }

  const handleResendInvite = async (inviteId: string) => {
    if (!accountId) return
    try {
      await accountService.resendInvitation(accountId, inviteId)
      toast({ title: 'Success', description: 'Invitation resent' })
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to resend invitation', variant: 'destructive' })
    }
  }

  const handleDeleteInvite = async (inviteId: string) => {
    if (!accountId || !confirm('Delete this invitation?')) return
    try {
      await accountService.deleteInvitation(accountId, inviteId)
      toast({ title: 'Success', description: 'Invitation deleted' })
      loadInvitations()
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete invitation', variant: 'destructive' })
    }
  }

  const handleArchive = async () => {
    if (!accountId || !confirm('Archive this account? It can be restored later.')) return
    try {
      await accountService.archiveAccount(accountId)
      toast({ title: 'Success', description: 'Account archived' })
      window.location.reload()
    } catch {
      toast({ title: 'Error', description: 'Failed to archive account', variant: 'destructive' })
    }
  }

  const handleRestore = async () => {
    if (!accountId || !confirm('Restore this account?')) return
    try {
      await accountService.restoreAccount(accountId)
      toast({ title: 'Success', description: 'Account restored' })
      window.location.reload()
    } catch {
      toast({ title: 'Error', description: 'Failed to restore account', variant: 'destructive' })
    }
  }

  if (isLoading) return <LoadingSpinner />
  if (error || !account) return <ErrorMessage message="Failed to load account details." />

  const isSuperUser = user?.isSuperUser || false
  const isActive = account.isActive

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/accounts">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{account.name}</h1>
            {!isActive && (
              <span className="text-destructive font-semibold">DISABLED / ARCHIVED</span>
            )}
          </div>
          {account.description && (
            <p className="text-muted-foreground mt-1">{account.description}</p>
          )}
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" asChild>
            <Link to={`/accounts/${accountId}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          {isSuperUser && (
            isActive ? (
              <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50" onClick={handleArchive}>
                <Archive className="mr-2 h-4 w-4" />
                Archive
              </Button>
            ) : (
              <Button variant="outline" className="text-green-600 border-green-300 hover:bg-green-50" onClick={handleRestore}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Restore
              </Button>
            )
          )}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
        {/* Left Column - Tabs */}
        <div>
          <Card>
            <CardContent className="p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="details">
                    <FileText className="h-4 w-4 mr-1" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="subaccounts">
                    <Building className="h-4 w-4 mr-1" />
                    Sub Accounts
                  </TabsTrigger>
                  <TabsTrigger value="users">
                    <Users className="h-4 w-4 mr-1" />
                    Users
                  </TabsTrigger>
                  <TabsTrigger value="weather">
                    <Cloud className="h-4 w-4 mr-1" />
                    Weather
                  </TabsTrigger>
                  <TabsTrigger value="logs">
                    <History className="h-4 w-4 mr-1" />
                    Logs
                  </TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Account Name</label>
                      <p className="text-lg">{account.name}</p>
                    </div>

                    {isSuperUser && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Account Type</label>
                        <div className="mt-1">
                          <AccountTypeBadge accountType={account.accountType} />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Description</label>
                      <p className="text-base">{account.description || 'No description'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Country</label>
                      <p className="text-base">{account.country || 'Not set'}</p>
                    </div>

                    {account.webhookUrl && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Webhook URL</label>
                        <p className="text-sm font-mono break-all">{account.webhookUrl}</p>
                      </div>
                    )}

                    <div className="pt-4 border-t">
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Created:</span>{' '}
                          {formatUTCToLocal(account.createdAt, 'PPP')}
                        </div>
                        <div>
                          <span className="font-medium">Updated:</span>{' '}
                          {formatUTCToLocal(account.updatedAt, 'PPP')}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4">
                      <Button asChild>
                        <Link to={`/accounts/${accountId}/edit`}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit Account
                        </Link>
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                {/* Sub Accounts Tab */}
                <TabsContent value="subaccounts" className="space-y-4 mt-4">
                  {loadingSubAccounts ? (
                    <LoadingSpinner />
                  ) : subAccounts.length > 0 ? (
                    <div className="space-y-2">
                      {subAccounts.map((subAccount) => (
                        <div
                          key={subAccount.id}
                          className="flex items-center justify-between p-3 rounded-lg border"
                        >
                          <div className="flex-1">
                            <Link
                              to={`/accounts/${subAccount.id}`}
                              className="font-medium hover:underline"
                            >
                              {subAccount.name}
                            </Link>
                            <div className="flex items-center gap-2 mt-1">
                              <AccountTypeBadge accountType={subAccount.accountType} />
                              {subAccount.country && (
                                <span className="text-sm text-muted-foreground">{subAccount.country}</span>
                              )}
                            </div>
                          </div>
                          {isSuperUser && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveSubAccount(subAccount.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Remove
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No sub-accounts linked</p>
                      {isSuperUser && (
                        <p className="text-sm mt-2">Super users can assign sub-accounts from the account list</p>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Users Tab */}
                <TabsContent value="users" className="space-y-4 mt-4">
                  {/* Invite User */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Invite a User</label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleInviteUser()}
                      />
                      <Button onClick={handleInviteUser}>
                        <Mail className="h-4 w-4 mr-2" />
                        Invite
                      </Button>
                    </div>
                  </div>

                  {/* Current Users */}
                  {loadingUsers ? (
                    <LoadingSpinner />
                  ) : accountUsers.length > 0 ? (
                    <div>
                      <h6 className="text-sm font-semibold mb-2">Current Users</h6>
                      <div className="space-y-2">
                        {accountUsers.map((accountUser) => (
                          <div
                            key={accountUser.id}
                            className="flex items-center justify-between p-3 rounded-lg border"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">
                                  {accountUser.displayName || `${accountUser.firstName} ${accountUser.lastName}`}
                                </p>
                                <UserRoleBadge isSuperUser={accountUser.isSuperUser} />
                                {accountUser.isAdmin && (
                                  <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                                    Account Admin
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{accountUser.email}</p>
                              {accountUser.lastLogin && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Last login: {formatUTCToLocal(accountUser.lastLogin, 'PPp')}
                                </p>
                              )}
                            </div>
                            {isSuperUser && (
                              <div className="flex gap-2">
                                {!accountUser.isSuperUser && (
                                  <Button
                                    variant={accountUser.isAdmin ? 'outline' : 'secondary'}
                                    size="sm"
                                    onClick={() => handleToggleAdmin(accountUser.id, accountUser.isAdmin)}
                                  >
                                    {accountUser.isAdmin ? (
                                      <>
                                        <UserX className="h-4 w-4 mr-1" />
                                        Revoke Admin
                                      </>
                                    ) : (
                                      <>
                                        <UserCheck className="h-4 w-4 mr-1" />
                                        Make Admin
                                      </>
                                    )}
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRemoveUser(accountUser.id)}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Remove
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <p className="text-sm">No users assigned to this account</p>
                    </div>
                  )}

                  {/* Invitations */}
                  {invitations.length > 0 && (
                    <div>
                      <h6 className="text-sm font-semibold mb-2">Invitations</h6>
                      <div className="space-y-2">
                        {invitations.map((invite) => (
                          <div
                            key={invite.id}
                            className="flex items-center justify-between p-3 rounded-lg border"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{invite.email}</p>
                                {invite.accepted ? (
                                  invite.userLinked ? (
                                    <Badge className="bg-green-100 text-green-800">Accepted</Badge>
                                  ) : (
                                    <Badge variant="destructive">User Removed</Badge>
                                  )
                                ) : invite.isExpired ? (
                                  <Badge variant="secondary">Expired</Badge>
                                ) : (
                                  <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Invited by {invite.inviterName} on {formatUTCToLocal(invite.createdAt, 'PPp')}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              {!invite.accepted && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleResendInvite(invite.id)}
                                >
                                  <RefreshCw className="h-4 w-4 mr-1" />
                                  Resend
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteInvite(invite.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Weather Tab */}
                <TabsContent value="weather" className="space-y-4 mt-4">
                  {loadingWeather ? (
                    <LoadingSpinner />
                  ) : weatherError ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Cloud className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-destructive font-medium">{weatherError}</p>
                      {weatherError.includes('GPS') && (
                        <p className="text-sm mt-2">Set weather GPS coordinates in account settings</p>
                      )}
                    </div>
                  ) : currentWeather && forecast ? (
                    <div className="space-y-6">
                      {/* Current Weather */}
                      <div>
                        <h6 className="text-sm font-semibold mb-3">Current Weather</h6>
                        <div className="flex items-center gap-6 p-4 rounded-lg border bg-card">
                          <div className="flex items-center gap-4">
                            {currentWeather.current.weather[0] && (
                              <img
                                src={`https://openweathermap.org/img/wn/${currentWeather.current.weather[0].icon}@2x.png`}
                                alt={currentWeather.current.weather[0].description}
                                className="w-20 h-20"
                              />
                            )}
                            <div>
                              <p className="text-4xl font-bold">
                                {Math.round(currentWeather.current.temp - 273.15)}°C
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Feels like {Math.round(currentWeather.current.feels_like - 273.15)}°C
                              </p>
                              {currentWeather.current.weather[0] && (
                                <p className="text-sm capitalize mt-1">
                                  {currentWeather.current.weather[0].description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex-1 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Wind</p>
                              <p className="font-medium">
                                {Math.round(currentWeather.current.wind_speed * 3.6)} km/h
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Humidity</p>
                              <p className="font-medium">{currentWeather.current.humidity}%</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Pressure</p>
                              <p className="font-medium">{currentWeather.current.pressure} hPa</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">UV Index</p>
                              <p className="font-medium">{currentWeather.current.uvi}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 7-Day Forecast */}
                      <div>
                        <h6 className="text-sm font-semibold mb-3">7-Day Forecast</h6>
                        <div className="grid gap-2">
                          {forecast.daily.slice(0, 7).map((day, index) => {
                            const date = new Date(day.dt * 1000)
                            const dayName = index === 0 ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' })
                            return (
                              <div
                                key={day.dt}
                                className="flex items-center justify-between p-3 rounded-lg border"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <p className="font-medium w-16">{dayName}</p>
                                  {day.weather[0] && (
                                    <img
                                      src={`https://openweathermap.org/img/wn/${day.weather[0].icon}.png`}
                                      alt={day.weather[0].description}
                                      className="w-10 h-10"
                                    />
                                  )}
                                  <p className="text-sm capitalize flex-1">
                                    {day.weather[0]?.description}
                                  </p>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <div className="text-right">
                                    <p className="font-medium">
                                      {Math.round(day.temp.max - 273.15)}°
                                    </p>
                                    <p className="text-muted-foreground">
                                      {Math.round(day.temp.min - 273.15)}°
                                    </p>
                                  </div>
                                  <div className="text-muted-foreground">
                                    <p>{Math.round(day.pop * 100)}% rain</p>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Cloud className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No weather data available</p>
                    </div>
                  )}
                </TabsContent>

                {/* Logs Tab */}
                <TabsContent value="logs" className="space-y-4 mt-4">
                  <AccountActivityLog accountId={accountId!} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Devices */}
        <div>
          <h6 className="text-sm font-semibold text-muted-foreground uppercase mb-3">Device List</h6>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Devices ({devices?.total || 0})</CardTitle>
                <Button size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Claim Device
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {devicesError ? (
                <div className="text-center py-12">
                  <p className="text-sm text-destructive">
                    Failed to load devices. Please try refreshing.
                  </p>
                </div>
              ) : devicesLoading ? (
                <LoadingSpinner />
              ) : devices?.devices && devices.devices.length > 0 ? (
                <div className="space-y-2">
                  {devices.devices.map((device) => (
                    <Link
                      key={device.id}
                      to={`/devices/${device.id}`}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{device.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {device.serialNumber}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {device.batteryLevel && (
                          <div className="text-right">
                            <p className="text-sm font-medium">{device.batteryLevel}%</p>
                            <p className="text-xs text-muted-foreground">Battery</p>
                          </div>
                        )}
                        {device.lastSeen && (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {formatUTCToLocal(device.lastSeen, 'MMM d')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatUTCToLocal(device.lastSeen, 'HH:mm')}
                            </p>
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No devices assigned to this account</p>
                  <p className="text-sm mt-2">Claim a device to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
